import { PhotoCredit } from "@/components/PhotoCredit";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CloudRain, Compass, Sparkles, Sun, Waves } from "lucide-react";
import { useGet } from "@/lib/api";
import { fmt, fmtTime, placeCredit, placeImage, weatherInfo } from "@/lib/meta";
import { Mosaic } from "@/components/CompassMosaic";
import { WattWarning } from "@/components/WattWarning";
import { Skeleton } from "@/components/ui/skeleton";

const TABS = [
  { key: "highlights", label: "Wochenend-Highlights", Icon: Sparkles },
  { key: "sonne", label: "Draußen erleben", Icon: Sun },
  { key: "regen", label: "Bei Regen drinnen", Icon: CloudRain },
  { key: "ebbe", label: "Bei Ebbe", Icon: Waves },
];

const WeatherStrip = ({ days, mode }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-foreground/10 py-4" data-testid="compass-weather">
    <p className="font-sub text-sm" data-testid="compass-verdict">
      {mode === "regen" ? "Eher nass am Wochenende – wir empfehlen Tipps für drinnen." : "Überwiegend trocken am Wochenende – ab nach draußen!"}
    </p>
    <div className="flex gap-5">
      {days.map((d) => {
        const { label, Icon } = weatherInfo(d.code);
        return (
          <span key={d.date} title={label} className="flex items-center gap-2 text-sm" data-testid={`compass-day-${d.date}`}>
            <span className="font-sub uppercase text-muted-foreground">{fmt(d.date, "EEE")}</span>
            <Icon size={18} strokeWidth={1.5} className="text-[#9B5D2E]" />
            {Math.round(d.max)}°<span className="text-xs text-ocean">{d.rain ?? 0}%</span>
          </span>
        );
      })}
    </div>
  </div>
);

const Ebbe = ({ data }) => (
  <div className="space-y-6" data-testid="compass-ebbe">
    <div className="grid gap-6 bg-ocean p-6 text-shell md:grid-cols-[220px_1fr_auto] md:items-center md:p-8">
      <div>
        <p className="eyebrow flex items-center gap-2 text-sand"><Waves size={14} /> Niedrigwasser tagsüber</p>
        <p className="mt-2 text-xs text-shell/60">{data.location}</p>
      </div>
      {data.tides.length ? (
        <div className="grid grid-cols-3 divide-x divide-shell/15">
          {data.tides.map((t) => (
            <p key={t.time} className="px-4 text-center first:pl-0 md:px-6" data-testid={`compass-low-${t.time}`}>
              <span className="block font-sub text-xs uppercase tracking-wider text-shell/70">{fmt(t.time, "EEEE")}</span>
              <span className="mt-1 block font-display text-3xl md:text-4xl">{fmtTime(t.time)}</span>
            </p>
          ))}
        </div>
      ) : <p className="text-sm text-shell/80">Am Wochenende kein Niedrigwasser zwischen 8 und 18 Uhr.</p>}
      <Link to="/gezeiten" data-testid="compass-tides-link" className="inline-flex items-center gap-2 font-sub text-sm text-sand hover:text-white">Alle Gezeiten <ArrowRight size={15} /></Link>
    </div>
    <WattWarning testid="compass-watt-warning" />
    {data.events.length > 0 && <Mosaic items={data.events} testid="compass-track-ebbe" />}
    {data.places.length > 0 && (
      <div>
        <p className="eyebrow mb-3 text-terracotta">Wattwandern in der Nähe</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.places.map((p) => (
            <Link key={p.id} to={`/orte/${p.id}`} data-testid={`compass-watt-place-${p.id}`} className="lift group flex items-center gap-4 border border-foreground/10 bg-white/60 p-3 hover:bg-white">
              {placeImage(p) && <img src={placeImage(p)} alt={p.name} loading="lazy" className="h-16 w-20 shrink-0 object-cover" />}
              <span className="min-w-0"><span className="block font-display text-lg leading-tight">{p.name}</span><span className="text-xs text-muted-foreground">{p.region}</span>
                {placeImage(p) && <PhotoCredit credit={placeCredit(p)} inLink className="mt-1" testid={`compass-watt-credit-${p.id}`} />}</span>
            </Link>
          ))}
        </div>
      </div>
    )}
  </div>
);

export const HomeCompass = () => {
  const { data } = useGet("home-compass", "/home/compass", {}, { staleTime: 900_000 });
  const [tab, setTab] = useState("highlights");
  useEffect(() => { if (data && !data.highlights.length) setTab(data.mode); }, [data]);
  return (
    <section className="mx-auto max-w-[1400px] px-5 pt-8 md:px-10 md:pt-12" data-testid="home-compass">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow flex items-center gap-2 text-terracotta"><Compass size={14} /> Kompass zum Wochenende</p>
          <h2 className="mt-2 text-3xl tracking-tight md:text-4xl">Was ist los kommendes Wochenende?</h2>
        </div>
        <Link to="/wochenende" data-testid="compass-weekend-link" className="group flex items-center gap-2 font-sub text-sm hover:text-terracotta">Ganzes Wochenende<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></Link>
      </div>
      {!data ? <Skeleton className="h-[340px]" /> : (
        <>
          {data.weather.length > 0 && <WeatherStrip days={data.weather} mode={data.mode} />}
          <div className="no-scrollbar -mx-5 my-6 flex gap-2 overflow-x-auto px-5 md:mx-0 md:flex-wrap md:px-0" role="tablist">
            {TABS.map(({ key, label, Icon }) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)} data-testid={`compass-tab-${key}`}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap border px-4 py-2.5 font-sub text-sm transition-colors ${tab === key ? "border-ocean bg-ocean text-shell" : "border-foreground/15 bg-white/50 hover:bg-white"}`}>
                <Icon size={16} strokeWidth={1.5} />{label}
                {key === data.mode && <span className="bg-terracotta px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-white">Passt zum Wetter</span>}
              </button>
            ))}
          </div>
          {tab === "ebbe" ? <Ebbe data={data.ebbe} /> : data[tab].length
            ? <Mosaic key={tab} items={data[tab]} testid={`compass-track-${tab}`} />
            : <p className="py-10 text-muted-foreground" data-testid="compass-empty">Für diese Auswahl gibt es am Wochenende noch keine Termine.</p>}
        </>
      )}
    </section>
  );
};
